import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Activity } from "lucide-react";

const ServerWarmup = ({ children }) => {
  const [status, setStatus] = useState("checking");

  const checkHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/health`, { timeout: 3000 });
      setStatus("ready");
    } catch {
      // Network error or timeout — still show the app (data loads lazily)
      setStatus("ready");
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // Auto-release after 3 seconds max — never block the user
  useEffect(() => {
    const timeout = setTimeout(() => setStatus("ready"), 3000);
    return () => clearTimeout(timeout);
  }, []);

  if (status === "checking") {
    return (
      <div data-testid="server-warmup" className="min-h-screen bg-slate-950">
        {/* Skeleton mimicking the app layout so it feels like content is painting in */}
        <div className="bg-[#0d0d0d] border-b border-gray-800 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="animate-pulse bg-gray-800/60 rounded h-6 w-6" />
            <div className="animate-pulse bg-gray-800/60 rounded h-5 w-32" />
          </div>
          <div className="flex items-center gap-4">
            {[1,2,3,4].map(i => <div key={i} className="animate-pulse bg-gray-800/60 rounded h-4 w-16" />)}
          </div>
        </div>
        <div className="max-w-[1920px] mx-auto p-4 space-y-4">
          {/* Ticker tape skeleton */}
          <div className="flex gap-4 overflow-hidden">
            {Array.from({length:12}).map((_,i) => (
              <div key={i} className="animate-pulse bg-gray-800/60 rounded h-5 w-24 flex-shrink-0" />
            ))}
          </div>
          {/* Indicator cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-[#111] border border-gray-800 rounded-lg p-3">
                <div className="animate-pulse bg-gray-800/60 rounded h-3 w-1/2 mb-2" />
                <div className="animate-pulse bg-gray-800/60 rounded h-7 w-2/3 mb-2" />
                <div className="animate-pulse bg-gray-800/60 rounded h-3 w-3/4" />
              </div>
            ))}
          </div>
          {/* Chart skeleton */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4 h-72">
            <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/4 mb-4" />
            <div className="flex items-end gap-1 h-[80%]">
              {Array.from({length:30}).map((_,i) => (
                <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*65}%`}} />
              ))}
            </div>
          </div>
          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[1,2,3].map(i => (
              <div key={i} className="bg-[#111] border border-gray-800 rounded-lg p-4">
                <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/3 mb-3" />
                {[1,2,3].map(j => <div key={j} className="animate-pulse bg-gray-800/60 rounded h-3 w-full mb-2" />)}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ServerWarmup;
